import { guessPhoneCountryFromPartialNumber, PhoneInput, toE164FromLegacyDigits, toLegacyDigitsFromE164 } from '@systemeio/components';
import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SafeHtmlText } from './safe-html-text';

/**
 * Country shown before any number is known. Matches the country the legacy phone field defaulted to.
 */
const FALLBACK_COUNTRY = 'FR';
interface FormInputPhoneProps {
  className?: string;
  disabled?: boolean;
  error?: string | null;
  label?: ReactNode;
  maxLength?: number;
  /** Receives the wire format: international digits without `+`. */
  onChange: (phone: string) => void;
  placeholder?: string;
  required?: boolean;
  /** Wire format: international digits without `+`. */
  value: string | undefined;
}
function FormInputPhone({
  className,
  disabled,
  error,
  label,
  maxLength,
  onChange,
  placeholder,
  required,
  value
}: FormInputPhoneProps) {
  const {
    i18n
  } = useTranslation();
  const phoneValue = toE164FromLegacyDigits(value);
  const [country, setCountry] = useState(() => guessPhoneCountryFromPartialNumber(phoneValue, FALLBACK_COUNTRY) ?? FALLBACK_COUNTRY);
  const knownValueRef = useRef(phoneValue);

  /*
   * Dashboard forms fill in asynchronously, so the first render usually has no number yet and a
   * static `defaultCountry` would stay wrong once the request resolves. Re-guess the country only
   * for values that did not come from this adapter's own emission — those already moved the country
   * through `onCountryChange`, and re-guessing them would fight a manual country choice.
   */
  if (knownValueRef.current !== phoneValue) {
    knownValueRef.current = phoneValue;
    const guessedCountry = guessPhoneCountryFromPartialNumber(phoneValue, country);
    if (guessedCountry && guessedCountry !== country) {
      setCountry(guessedCountry);
    }
  }
  return <PhoneInput.Root className={className} country={country} disabled={disabled} invalid={Boolean(error)} locale={i18n?.language} onCountryChange={setCountry} onValueChange={nextValue => {
    knownValueRef.current = nextValue;
    onChange(toLegacyDigitsFromE164(nextValue));
  }} required={required} value={phoneValue}>
      {label !== undefined && label !== null && label !== false && <PhoneInput.Label>{label}</PhoneInput.Label>}
      <PhoneInput.Control maxLength={maxLength} placeholder={placeholder} />
      <PhoneInput.Error hidden={!error}>
        {error ? <SafeHtmlText as="p" html={error} /> : null}
      </PhoneInput.Error>
    </PhoneInput.Root>;
}
export { FormInputPhone };
export type { FormInputPhoneProps };